import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CompanySettingsData, companySettingsService } from '@/services/companySettings.service';
import { QUERY_KEYS } from '@/utils/constants';

export const useCompanySettings = () =>
  useQuery({
    queryKey: QUERY_KEYS.COMPANY_SETTINGS.ALL,
    queryFn: companySettingsService.getAll,
  });

// Available to any authenticated user (non-admin), unlike useCompanySettings.
export const useFinancialYear = () =>
  useQuery({
    queryKey: QUERY_KEYS.COMPANY_SETTINGS.FINANCIAL_YEAR,
    queryFn: companySettingsService.getFinancialYear,
  });

// Employee designation level/department dropdowns (non-admin). Read by the
// onboarding form, the profile editor and the admin user editor, which must all
// offer the same vocabulary or an employee's department depends on which screen
// created them.
export const useDesignations = () =>
  useQuery({
    queryKey: [...QUERY_KEYS.COMPANY_SETTINGS.ALL, 'designations'],
    queryFn: companySettingsService.getDesignations,
    staleTime: 5 * 60 * 1000,
  });

// Bank-account labels for the email bank selector (non-admin, see ADR-020).
export const useBankAccounts = () =>
  useQuery({
    queryKey: QUERY_KEYS.COMPANY_SETTINGS.BANK_ACCOUNTS,
    queryFn: companySettingsService.getBankAccounts,
    staleTime: 5 * 60 * 1000,
  });

// Configured sender labels + emails for the RM's From-address dropdown.
export const useEmailSenders = () =>
  useQuery({
    queryKey: [...QUERY_KEYS.COMPANY_SETTINGS.ALL, 'email-senders'],
    queryFn: companySettingsService.getEmailSenders,
    staleTime: 5 * 60 * 1000,
  });

// Admin-configured day count for the initial payment installment span (ADR-035).
// Available to any authenticated user (Sales RM needs it in the wizard).
export const usePaymentInstallmentDays = () =>
  useQuery({
    queryKey: [...QUERY_KEYS.COMPANY_SETTINGS.ALL, 'payment-installment-days'],
    queryFn: companySettingsService.getPaymentInstallmentDays,
    staleTime: 5 * 60 * 1000,
  });

// Record-keeping CC addresses per template category (non-admin). Displayed as
// locked chips next to the composer's CC box; the server does the enforcing.
export const useRecordKeepingCc = () =>
  useQuery({
    queryKey: [...QUERY_KEYS.COMPANY_SETTINGS.ALL, 'record-cc'],
    queryFn: companySettingsService.getRecordKeepingCc,
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateCompanySettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanySettingsData) => companySettingsService.update(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.COMPANY_SETTINGS.ALL });
    },
  });
};
